import { logisticsConditionRatingToGerman, logisticsVisualDetailsPollutionToGerman } from '../translations'
import type { ApiLogisticsTaskGetByCuid, LogisticsConditionRating, LogisticsTaskGetByCuidPositionToShip, LogisticsVisualDetailsPollution, MachineryCategories, MachineryDrives } from '~/types'

const renderBoolean = (value: boolean | undefined | null) => value === true ? 'Ja' : value === false ? 'Nein' : ''

/**
 * We seperated these table configs as they broke the pages they were used on otherwise, starting with the Nuxt 3.17 update: https://github.com/sidestream-tech/hanselmann-os/pull/4136#issuecomment-2893650156
 *
 * Later on, this may be refactored into a separate composable (as part of a bigger shift into broken down table configs). There's no specific other reason than fixing a bug to have it separate,
 * so if in the future this bug doesn't occur anymore, it could also be merged back in with the others.
 */
export default function () {
  const i18n = useI18n()

  const {
    logisticsMachineryDetailsFieldsByMachineCategory,
    logisticsFunctionalDetailsFieldsByMachineCategory,
    logisticsVisualDetailsFieldsByMachineCategory,
    logisticsConditionDetailsFieldsByMachineCategory,
  } = useFormOptionalConditions()

    type LogisticsTaskItemSetMemberPosition = Exclude<ApiLogisticsTaskGetByCuid['positionsToShip'][number]['itemSetMember'][number], null>

    const logisticsIssuanceAndMachineryDetails: OneDataColumn<LogisticsTaskGetByCuidPositionToShip>[] = [
      {
        title: 'Geräte Nr.',
        key: 'id',
        minWidth: 200,
        render: ({ machinery }) => machinery?.id,
      },
      {
        title: 'Gerätedaten',
        key: 'id',
        isHeading: true,
      },
      {
        title: 'Tankfüllung (Liter)',
        key: 'outboundLogisticsMachineryDetails',
        render: ({ outboundLogisticsMachineryDetails }) => outboundLogisticsMachineryDetails?.tankFilling?.toLocaleString('de-DE'),
        isVisible: ({ machinery, outboundLogisticsMachineryDetails }) => machinery
          ? Boolean(outboundLogisticsMachineryDetails?.tankFilling) && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'tankFilling', machinery?.category as MachineryCategories, machinery?.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'AdBlue (Liter)',
        key: 'outboundLogisticsMachineryDetails',
        render: ({ outboundLogisticsMachineryDetails }) => outboundLogisticsMachineryDetails?.adblueFilling?.toLocaleString('de-DE'),
        isVisible: ({ machinery, outboundLogisticsMachineryDetails }) => machinery
          ? Boolean(outboundLogisticsMachineryDetails?.adblueFilling) && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'adblueFilling', machinery?.category as MachineryCategories, machinery?.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Tank ist voll',
        key: 'outboundLogisticsMachineryDetails',
        render: ({ outboundLogisticsMachineryDetails }) => renderBoolean(outboundLogisticsMachineryDetails?.isTankFull),
        isVisible: ({ machinery, outboundLogisticsMachineryDetails }) => machinery
          ? typeof outboundLogisticsMachineryDetails?.isTankFull === 'boolean' && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'isTankFull', machinery?.category as MachineryCategories, machinery?.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Betriebsstunden Zähler (Stunden)',
        key: 'outboundLogisticsMachineryDetails',
        render: ({ outboundLogisticsMachineryDetails }) => outboundLogisticsMachineryDetails?.operatingHours?.toLocaleString('de-DE'),
        isVisible: ({ machinery, outboundLogisticsMachineryDetails }) => machinery
          ? Boolean(outboundLogisticsMachineryDetails?.operatingHours) && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'operatingHours', machinery?.category as MachineryCategories, machinery?.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Zündschlüssel (Anzahl)',
        key: 'outboundLogisticsMachineryDetails',
        render: ({ outboundLogisticsMachineryDetails }) => outboundLogisticsMachineryDetails?.ignitionKeys?.toLocaleString('de-DE'),
        isVisible: ({ machinery, outboundLogisticsMachineryDetails }) => machinery
          ? Boolean(outboundLogisticsMachineryDetails?.ignitionKeys) && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'ignitionKeys', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: i18n.t('logisticsMachineryDetails.gasTanks.label'),
        key: 'outboundLogisticsMachineryDetails',
        render: ({ outboundLogisticsMachineryDetails }) => outboundLogisticsMachineryDetails?.gasTanks?.toLocaleString('de-DE'),
        isVisible: ({ machinery, outboundLogisticsMachineryDetails }) => machinery
          ? Boolean(outboundLogisticsMachineryDetails?.gasTanks) && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'gasTanks', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'TÜV',
        key: 'outboundLogisticsMachineryDetails',
        render: ({ outboundLogisticsMachineryDetails }) => {
          const date = outboundLogisticsMachineryDetails?.tuvValiditedAt
          return date ? useDateAsString(date) : 'N/A'
        },
        isVisible: ({ machinery, outboundLogisticsMachineryDetails }) => machinery
          ? Boolean(outboundLogisticsMachineryDetails?.tuvValiditedAt) && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'tuvValiditedAt', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'SP',
        key: 'outboundLogisticsMachineryDetails',
        render: ({ outboundLogisticsMachineryDetails }) => {
          const date = outboundLogisticsMachineryDetails?.securityCheckValiditedAt
          return date ? useDateAsString(date) : 'N/A'
        },
        isVisible: ({ machinery, outboundLogisticsMachineryDetails }) => machinery
          ? Boolean(outboundLogisticsMachineryDetails?.securityCheckValiditedAt) && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'securityCheckValiditedAt', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Funktionsprüfung',
        key: 'outboundLogisticsFunctionalDetails',
        isHeading: true,
      },
      {
        title: 'Motor, Getriebe',
        key: 'outboundLogisticsFunctionalDetails',
        render: ({ outboundLogisticsFunctionalDetails }) => renderBoolean(outboundLogisticsFunctionalDetails?.areEngineAndGearboxWorking),
        isVisible: ({ machinery, outboundLogisticsFunctionalDetails }) => machinery
          ? typeof outboundLogisticsFunctionalDetails?.areEngineAndGearboxWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'areEngineAndGearboxWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Bremsen',
        key: 'outboundLogisticsFunctionalDetails',
        render: ({ outboundLogisticsFunctionalDetails }) => renderBoolean(outboundLogisticsFunctionalDetails?.isBreakWorking),
        isVisible: ({ machinery, outboundLogisticsFunctionalDetails }) => machinery
          ? typeof outboundLogisticsFunctionalDetails?.isBreakWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isBreakWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Lenkung',
        key: 'outboundLogisticsFunctionalDetails',
        render: ({ outboundLogisticsFunctionalDetails }) => renderBoolean(outboundLogisticsFunctionalDetails?.isSteerwheelWorking),
        isVisible: ({ machinery, outboundLogisticsFunctionalDetails }) => machinery
          ? typeof outboundLogisticsFunctionalDetails?.isSteerwheelWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isSteerwheelWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Hubgerüst, Kette, Lager',
        key: 'outboundLogisticsFunctionalDetails',
        render: ({ outboundLogisticsFunctionalDetails }) => renderBoolean(outboundLogisticsFunctionalDetails?.areMastAndChainAndBearingWorking),
        isVisible: ({ machinery, outboundLogisticsFunctionalDetails }) => machinery
          ? typeof outboundLogisticsFunctionalDetails?.areMastAndChainAndBearingWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'areMastAndChainAndBearingWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Elektrische Anlage',
        key: 'outboundLogisticsFunctionalDetails',
        render: ({ outboundLogisticsFunctionalDetails }) => renderBoolean(outboundLogisticsFunctionalDetails?.isElectricalSystemWorking),
        isVisible: ({ machinery, outboundLogisticsFunctionalDetails }) => machinery
          ? typeof outboundLogisticsFunctionalDetails?.isElectricalSystemWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isElectricalSystemWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Anbaugeräte',
        key: 'outboundLogisticsFunctionalDetails',
        render: ({ outboundLogisticsFunctionalDetails }) => renderBoolean(outboundLogisticsFunctionalDetails?.isMachineryAccessory),
        isVisible: ({ machinery, outboundLogisticsFunctionalDetails }) => machinery
          ? typeof outboundLogisticsFunctionalDetails?.isMachineryAccessory === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isMachineryAccessory', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Sicherheitseinrichtung',
        key: 'outboundLogisticsFunctionalDetails',
        render: ({ outboundLogisticsFunctionalDetails }) => renderBoolean(outboundLogisticsFunctionalDetails?.isSecurityEquipmentWorking),
        isVisible: ({ machinery, outboundLogisticsFunctionalDetails }) => machinery
          ? typeof outboundLogisticsFunctionalDetails?.isSecurityEquipmentWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isSecurityEquipmentWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Beleuchtung',
        key: 'outboundLogisticsFunctionalDetails',
        render: ({ outboundLogisticsFunctionalDetails }) => renderBoolean(outboundLogisticsFunctionalDetails?.isLightingWorking),
        isVisible: ({ machinery, outboundLogisticsFunctionalDetails }) => machinery
          ? typeof outboundLogisticsFunctionalDetails?.isLightingWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isLightingWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Batterie',
        key: 'outboundLogisticsFunctionalDetails',
        render: ({ outboundLogisticsFunctionalDetails }) => renderBoolean(outboundLogisticsFunctionalDetails?.isBatteryWorking),
        isVisible: ({ machinery, outboundLogisticsFunctionalDetails }) => machinery
          ? typeof outboundLogisticsFunctionalDetails?.isBatteryWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isBatteryWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Warnlicht, Hupe',
        key: 'outboundLogisticsFunctionalDetails',
        render: ({ outboundLogisticsFunctionalDetails }) => renderBoolean(outboundLogisticsFunctionalDetails?.areWarningLightAndHornWorking),
        isVisible: ({ machinery, outboundLogisticsFunctionalDetails }) => machinery
          ? typeof outboundLogisticsFunctionalDetails?.areWarningLightAndHornWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'areWarningLightAndHornWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Sicherheitsabschaltung',
        key: 'outboundLogisticsFunctionalDetails',
        render: ({ outboundLogisticsFunctionalDetails }) => renderBoolean(outboundLogisticsFunctionalDetails?.isSafetyShutdownWorking),
        isVisible: ({ machinery, outboundLogisticsFunctionalDetails }) => machinery
          ? typeof outboundLogisticsFunctionalDetails?.isSafetyShutdownWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isSafetyShutdownWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Hydrauliksystem',
        key: 'outboundLogisticsFunctionalDetails',
        render: ({ outboundLogisticsFunctionalDetails }) => renderBoolean(outboundLogisticsFunctionalDetails?.isHydraulicSystemWorking),
        isVisible: ({ machinery, outboundLogisticsFunctionalDetails }) => machinery
          ? typeof outboundLogisticsFunctionalDetails?.isHydraulicSystemWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isHydraulicSystemWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'GPS in Ordnung',
        key: 'outboundLogisticsFunctionalDetails',
        render: ({ outboundLogisticsFunctionalDetails }) => renderBoolean(outboundLogisticsFunctionalDetails?.isGpsWorking),
        isVisible: ({ machinery, outboundLogisticsFunctionalDetails }) => machinery
          ? typeof outboundLogisticsFunctionalDetails?.isGpsWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isGpsWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Bemerkung',
        key: 'outboundLogisticsFunctionalDetails',
        render: ({ outboundLogisticsFunctionalDetails }) => outboundLogisticsFunctionalDetails?.note,
        isVisible: ({ machinery, outboundLogisticsFunctionalDetails }) => machinery
          ? Boolean(outboundLogisticsFunctionalDetails?.note) && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'note', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Sichtprüfung',
        key: 'outboundLogisticsVisualDetails',
        isHeading: true,
      },
      {
        title: 'Gerät komplett',
        key: 'outboundLogisticsVisualDetails',
        render: ({ outboundLogisticsVisualDetails }) => renderBoolean(outboundLogisticsVisualDetails?.isMachineryComplete),
        isVisible: ({ machinery, outboundLogisticsVisualDetails }) => machinery
          ? typeof outboundLogisticsVisualDetails?.isMachineryComplete === 'boolean' && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'isMachineryComplete', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Äußere Schäden',
        key: 'outboundLogisticsVisualDetails',
        render: ({ outboundLogisticsVisualDetails }) => renderBoolean(outboundLogisticsVisualDetails?.hasExternalDamage),
        isVisible: ({ machinery, outboundLogisticsVisualDetails }) => machinery
          ? typeof outboundLogisticsVisualDetails?.hasExternalDamage === 'boolean' && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'hasExternalDamage', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Ölstand OK',
        key: 'outboundLogisticsVisualDetails',
        render: ({ outboundLogisticsVisualDetails }) => renderBoolean(outboundLogisticsVisualDetails?.isOilLevelOk),
        isVisible: ({ machinery, outboundLogisticsVisualDetails }) => machinery
          ? typeof outboundLogisticsVisualDetails?.isOilLevelOk === 'boolean' && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'isOilLevelOk', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Kühlwasserstand OK',
        key: 'outboundLogisticsVisualDetails',
        render: ({ outboundLogisticsVisualDetails }) => renderBoolean(outboundLogisticsVisualDetails?.isCoolantLevelOk),
        isVisible: ({ machinery, outboundLogisticsVisualDetails }) => machinery
          ? typeof outboundLogisticsVisualDetails?.isCoolantLevelOk === 'boolean' && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'isCoolantLevelOk', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Leckagen',
        key: 'outboundLogisticsVisualDetails',
        render: ({ outboundLogisticsVisualDetails }) => renderBoolean(outboundLogisticsVisualDetails?.isLeaking),
        isVisible: ({ machinery, outboundLogisticsVisualDetails }) => machinery
          ? typeof outboundLogisticsVisualDetails?.isLeaking === 'boolean' && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'isLeaking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Gerät gereinigt',
        key: 'outboundLogisticsVisualDetails',
        render: ({ outboundLogisticsVisualDetails }) => renderBoolean(outboundLogisticsVisualDetails?.isMachineryCleaned),
        isVisible: ({ machinery, outboundLogisticsVisualDetails }) => machinery
          ? typeof outboundLogisticsVisualDetails?.isMachineryCleaned === 'boolean' && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'isMachineryCleaned', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Verschmutzung',
        key: 'outboundLogisticsVisualDetails',
        render: ({ outboundLogisticsVisualDetails }) => logisticsVisualDetailsPollutionToGerman[outboundLogisticsVisualDetails?.pollution as LogisticsVisualDetailsPollution],
        isVisible: ({ machinery, outboundLogisticsVisualDetails }) => machinery
          ? Boolean(outboundLogisticsVisualDetails?.pollution) && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'pollution', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Masseband vorhanden',
        key: 'outboundLogisticsVisualDetails',
        render: ({ outboundLogisticsVisualDetails }) => renderBoolean(outboundLogisticsVisualDetails?.isTapeMeasureAvailable),
        isVisible: ({ machinery, outboundLogisticsVisualDetails }) => machinery
          ? typeof outboundLogisticsVisualDetails?.isTapeMeasureAvailable === 'boolean' && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'isTapeMeasureAvailable', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Bemerkung',
        key: 'outboundLogisticsVisualDetails',
        render: ({ outboundLogisticsVisualDetails }) => outboundLogisticsVisualDetails?.note,
        isVisible: ({ machinery, outboundLogisticsVisualDetails }) => machinery
          ? Boolean(outboundLogisticsVisualDetails?.note) && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'note', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Zustand',
        key: 'outboundLogisticsConditionDetails',
        isHeading: true,
      },
      {
        title: 'Reifen Luft / SE',
        key: 'outboundLogisticsConditionDetails',
        render: ({ outboundLogisticsConditionDetails }) => logisticsConditionRatingToGerman[outboundLogisticsConditionDetails?.tiresAirAndSE as LogisticsConditionRating],
        isVisible: ({ machinery, outboundLogisticsConditionDetails }) => machinery
          ? Boolean(outboundLogisticsConditionDetails?.tiresAirAndSE) && useShowFieldByFilters(logisticsConditionDetailsFieldsByMachineCategory, 'tiresAirAndSE', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Gabeln / Schaufeln',
        key: 'outboundLogisticsConditionDetails',
        render: ({ outboundLogisticsConditionDetails }) => logisticsConditionRatingToGerman[outboundLogisticsConditionDetails?.forkesAndShovels as LogisticsConditionRating],
        isVisible: ({ machinery, outboundLogisticsConditionDetails }) => machinery
          ? Boolean(outboundLogisticsConditionDetails?.forkesAndShovels) && useShowFieldByFilters(logisticsConditionDetailsFieldsByMachineCategory, 'forkesAndShovels', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Lackierung',
        key: 'outboundLogisticsConditionDetails',
        render: ({ outboundLogisticsConditionDetails }) => logisticsConditionRatingToGerman[outboundLogisticsConditionDetails?.painting as LogisticsConditionRating],
        isVisible: ({ machinery, outboundLogisticsConditionDetails }) => machinery
          ? Boolean(outboundLogisticsConditionDetails?.painting) && useShowFieldByFilters(logisticsConditionDetailsFieldsByMachineCategory, 'painting', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Fahrersitz',
        key: 'outboundLogisticsConditionDetails',
        render: ({ outboundLogisticsConditionDetails }) => logisticsConditionRatingToGerman[outboundLogisticsConditionDetails?.driverSeat as LogisticsConditionRating],
        isVisible: ({ machinery, outboundLogisticsConditionDetails }) => machinery
          ? Boolean(outboundLogisticsConditionDetails?.driverSeat) && useShowFieldByFilters(logisticsConditionDetailsFieldsByMachineCategory, 'driverSeat', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Bemerkung',
        key: 'outboundLogisticsConditionDetails',
        render: ({ outboundLogisticsConditionDetails }) => outboundLogisticsConditionDetails?.note,
        isVisible: ({ machinery, outboundLogisticsConditionDetails }) => machinery
          ? Boolean(outboundLogisticsConditionDetails?.note) && useShowFieldByFilters(logisticsConditionDetailsFieldsByMachineCategory, 'note', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
    ]

    const logisticsReturnAndMachineryDetails: OneDataColumn<LogisticsTaskGetByCuidPositionToShip>[] = [
      {
        title: 'Geräte Nr.',
        key: 'id',
        minWidth: 200,
        render: ({ machinery }) => machinery?.id,
      },
      {
        title: 'Gerätedaten',
        key: 'id',
        isHeading: true,
      },
      {
        title: 'Tankfüllung (Liter)',
        key: 'inboundLogisticsMachineryDetails',
        render: ({ inboundLogisticsMachineryDetails }) => inboundLogisticsMachineryDetails?.tankFilling?.toLocaleString('de-DE'),
        isVisible: ({ machinery, inboundLogisticsMachineryDetails }) => machinery
          ? Boolean(inboundLogisticsMachineryDetails?.tankFilling) && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'tankFilling', machinery?.category as MachineryCategories, machinery?.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'AdBlue (Liter)',
        key: 'inboundLogisticsMachineryDetails',
        render: ({ inboundLogisticsMachineryDetails }) => inboundLogisticsMachineryDetails?.adblueFilling?.toLocaleString('de-DE'),
        isVisible: ({ machinery, inboundLogisticsMachineryDetails }) => machinery
          ? Boolean(inboundLogisticsMachineryDetails?.adblueFilling) && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'adblueFilling', machinery?.category as MachineryCategories, machinery?.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Tank ist voll',
        key: 'inboundLogisticsMachineryDetails',
        render: ({ inboundLogisticsMachineryDetails }) => renderBoolean(inboundLogisticsMachineryDetails?.isTankFull),
        isVisible: ({ machinery, inboundLogisticsMachineryDetails }) => machinery
          ? typeof inboundLogisticsMachineryDetails?.isTankFull === 'boolean' && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'isTankFull', machinery?.category as MachineryCategories, machinery?.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Betriebsstunden Zähler (Stunden)',
        key: 'inboundLogisticsMachineryDetails',
        render: ({ inboundLogisticsMachineryDetails }) => inboundLogisticsMachineryDetails?.operatingHours?.toLocaleString('de-DE'),
        isVisible: ({ machinery, inboundLogisticsMachineryDetails }) => machinery
          ? Boolean(inboundLogisticsMachineryDetails?.operatingHours) && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'operatingHours', machinery?.category as MachineryCategories, machinery?.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Zündschlüssel (Anzahl)',
        key: 'inboundLogisticsMachineryDetails',
        render: ({ inboundLogisticsMachineryDetails }) => inboundLogisticsMachineryDetails?.ignitionKeys?.toLocaleString('de-DE'),
        isVisible: ({ machinery, inboundLogisticsMachineryDetails }) => machinery
          ? Boolean(inboundLogisticsMachineryDetails?.ignitionKeys) && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'ignitionKeys', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: i18n.t('logisticsMachineryDetails.gasTanks.label'),
        key: 'inboundLogisticsMachineryDetails',
        render: ({ inboundLogisticsMachineryDetails }) => inboundLogisticsMachineryDetails?.gasTanks?.toLocaleString('de-DE'),
        isVisible: ({ machinery, inboundLogisticsMachineryDetails }) => machinery
          ? Boolean(inboundLogisticsMachineryDetails?.gasTanks) && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'gasTanks', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: i18n.t('logisticsMachineryDetails.emptyGasTanks.label'),
        key: 'inboundLogisticsMachineryDetails',
        render: ({ inboundLogisticsMachineryDetails }) => inboundLogisticsMachineryDetails?.emptyGasTanks?.toLocaleString('de-DE'),
        isVisible: ({ machinery, inboundLogisticsMachineryDetails }) => machinery
          ? Boolean(inboundLogisticsMachineryDetails?.emptyGasTanks) && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'emptyGasTanks', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'TÜV',
        key: 'inboundLogisticsMachineryDetails',
        render: ({ inboundLogisticsMachineryDetails }) => {
          const date = inboundLogisticsMachineryDetails?.tuvValiditedAt
          return date ? useDateAsString(date) : 'N/A'
        },
        isVisible: ({ machinery, inboundLogisticsMachineryDetails }) => machinery
          ? Boolean(inboundLogisticsMachineryDetails?.tuvValiditedAt) && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'tuvValiditedAt', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'SP',
        key: 'inboundLogisticsMachineryDetails',
        render: ({ inboundLogisticsMachineryDetails }) => {
          const date = inboundLogisticsMachineryDetails?.securityCheckValiditedAt
          return date ? useDateAsString(date) : 'N/A'
        },
        isVisible: ({ machinery, inboundLogisticsMachineryDetails }) => machinery
          ? Boolean(inboundLogisticsMachineryDetails?.securityCheckValiditedAt) && useShowFieldByFilters(logisticsMachineryDetailsFieldsByMachineCategory, 'securityCheckValiditedAt', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Funktionsprüfung',
        key: 'inboundLogisticsFunctionalDetails',
        isHeading: true,
      },
      {
        title: 'Motor, Getriebe',
        key: 'inboundLogisticsFunctionalDetails',
        render: ({ inboundLogisticsFunctionalDetails }) => renderBoolean(inboundLogisticsFunctionalDetails?.areEngineAndGearboxWorking),
        isVisible: ({ machinery, inboundLogisticsFunctionalDetails }) => machinery
          ? typeof inboundLogisticsFunctionalDetails?.areEngineAndGearboxWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'areEngineAndGearboxWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Bremsen',
        key: 'inboundLogisticsFunctionalDetails',
        render: ({ inboundLogisticsFunctionalDetails }) => renderBoolean(inboundLogisticsFunctionalDetails?.isBreakWorking),
        isVisible: ({ machinery, inboundLogisticsFunctionalDetails }) => machinery
          ? typeof inboundLogisticsFunctionalDetails?.isBreakWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isBreakWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Lenkung',
        key: 'inboundLogisticsFunctionalDetails',
        render: ({ inboundLogisticsFunctionalDetails }) => renderBoolean(inboundLogisticsFunctionalDetails?.isSteerwheelWorking),
        isVisible: ({ machinery, inboundLogisticsFunctionalDetails }) => machinery
          ? typeof inboundLogisticsFunctionalDetails?.isSteerwheelWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isSteerwheelWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Hubgerüst, Kette, Lager',
        key: 'inboundLogisticsFunctionalDetails',
        render: ({ inboundLogisticsFunctionalDetails }) => renderBoolean(inboundLogisticsFunctionalDetails?.areMastAndChainAndBearingWorking),
        isVisible: ({ machinery, inboundLogisticsFunctionalDetails }) => machinery
          ? typeof inboundLogisticsFunctionalDetails?.areMastAndChainAndBearingWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'areMastAndChainAndBearingWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Elektrische Anlage',
        key: 'inboundLogisticsFunctionalDetails',
        render: ({ inboundLogisticsFunctionalDetails }) => renderBoolean(inboundLogisticsFunctionalDetails?.isElectricalSystemWorking),
        isVisible: ({ machinery, inboundLogisticsFunctionalDetails }) => machinery
          ? typeof inboundLogisticsFunctionalDetails?.isElectricalSystemWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isElectricalSystemWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Anbaugeräte',
        key: 'inboundLogisticsFunctionalDetails',
        render: ({ inboundLogisticsFunctionalDetails }) => renderBoolean(inboundLogisticsFunctionalDetails?.isMachineryAccessory),
        isVisible: ({ machinery, inboundLogisticsFunctionalDetails }) => machinery
          ? typeof inboundLogisticsFunctionalDetails?.isMachineryAccessory === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isMachineryAccessory', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Sicherheitseinrichtung',
        key: 'inboundLogisticsFunctionalDetails',
        render: ({ inboundLogisticsFunctionalDetails }) => renderBoolean(inboundLogisticsFunctionalDetails?.isSecurityEquipmentWorking),
        isVisible: ({ machinery, inboundLogisticsFunctionalDetails }) => machinery
          ? typeof inboundLogisticsFunctionalDetails?.isSecurityEquipmentWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isSecurityEquipmentWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Beleuchtung',
        key: 'inboundLogisticsFunctionalDetails',
        render: ({ inboundLogisticsFunctionalDetails }) => renderBoolean(inboundLogisticsFunctionalDetails?.isLightingWorking),
        isVisible: ({ machinery, inboundLogisticsFunctionalDetails }) => machinery
          ? typeof inboundLogisticsFunctionalDetails?.isLightingWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isLightingWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Batterie',
        key: 'inboundLogisticsFunctionalDetails',
        render: ({ inboundLogisticsFunctionalDetails }) => renderBoolean(inboundLogisticsFunctionalDetails?.isBatteryWorking),
        isVisible: ({ machinery, inboundLogisticsFunctionalDetails }) => machinery
          ? typeof inboundLogisticsFunctionalDetails?.isBatteryWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isBatteryWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Warnlicht, Hupe',
        key: 'inboundLogisticsFunctionalDetails',
        render: ({ inboundLogisticsFunctionalDetails }) => renderBoolean(inboundLogisticsFunctionalDetails?.areWarningLightAndHornWorking),
        isVisible: ({ machinery, inboundLogisticsFunctionalDetails }) => machinery
          ? typeof inboundLogisticsFunctionalDetails?.areWarningLightAndHornWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'areWarningLightAndHornWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Sicherheitsabschaltung',
        key: 'inboundLogisticsFunctionalDetails',
        render: ({ inboundLogisticsFunctionalDetails }) => renderBoolean(inboundLogisticsFunctionalDetails?.isSafetyShutdownWorking),
        isVisible: ({ machinery, inboundLogisticsFunctionalDetails }) => machinery
          ? typeof inboundLogisticsFunctionalDetails?.isSafetyShutdownWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isSafetyShutdownWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Hydrauliksystem',
        key: 'inboundLogisticsFunctionalDetails',
        render: ({ inboundLogisticsFunctionalDetails }) => renderBoolean(inboundLogisticsFunctionalDetails?.isHydraulicSystemWorking),
        isVisible: ({ machinery, inboundLogisticsFunctionalDetails }) => machinery
          ? typeof inboundLogisticsFunctionalDetails?.isHydraulicSystemWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isHydraulicSystemWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'GPS in Ordnung',
        key: 'inboundLogisticsFunctionalDetails',
        render: ({ inboundLogisticsFunctionalDetails }) => renderBoolean(inboundLogisticsFunctionalDetails?.isGpsWorking),
        isVisible: ({ machinery, inboundLogisticsFunctionalDetails }) => machinery
          ? typeof inboundLogisticsFunctionalDetails?.isGpsWorking === 'boolean' && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'isGpsWorking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Bemerkung',
        key: 'inboundLogisticsFunctionalDetails',
        render: ({ inboundLogisticsFunctionalDetails }) => inboundLogisticsFunctionalDetails?.note,
        isVisible: ({ machinery, inboundLogisticsFunctionalDetails }) => machinery
          ? Boolean(inboundLogisticsFunctionalDetails?.note) && useShowFieldByFilters(logisticsFunctionalDetailsFieldsByMachineCategory, 'note', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Sichtprüfung',
        key: 'inboundLogisticsVisualDetails',
        isHeading: true,
      },
      {
        title: 'Gerät komplett',
        key: 'inboundLogisticsVisualDetails',
        render: ({ inboundLogisticsVisualDetails }) => renderBoolean(inboundLogisticsVisualDetails?.isMachineryComplete),
        isVisible: ({ machinery, inboundLogisticsVisualDetails }) => machinery
          ? typeof inboundLogisticsVisualDetails?.isMachineryComplete === 'boolean' && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'isMachineryComplete', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Äußere Schäden',
        key: 'inboundLogisticsVisualDetails',
        render: ({ inboundLogisticsVisualDetails }) => renderBoolean(inboundLogisticsVisualDetails?.hasExternalDamage),
        isVisible: ({ machinery, inboundLogisticsVisualDetails }) => machinery
          ? typeof inboundLogisticsVisualDetails?.hasExternalDamage === 'boolean' && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'hasExternalDamage', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Ölstand OK',
        key: 'inboundLogisticsVisualDetails',
        render: ({ inboundLogisticsVisualDetails }) => renderBoolean(inboundLogisticsVisualDetails?.isOilLevelOk),
        isVisible: ({ machinery, inboundLogisticsVisualDetails }) => machinery
          ? typeof inboundLogisticsVisualDetails?.isOilLevelOk === 'boolean' && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'isOilLevelOk', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Kühlwasserstand OK',
        key: 'inboundLogisticsVisualDetails',
        render: ({ inboundLogisticsVisualDetails }) => renderBoolean(inboundLogisticsVisualDetails?.isCoolantLevelOk),
        isVisible: ({ machinery, inboundLogisticsVisualDetails }) => machinery
          ? typeof inboundLogisticsVisualDetails?.isCoolantLevelOk === 'boolean' && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'isCoolantLevelOk', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Leckagen',
        key: 'inboundLogisticsVisualDetails',
        render: ({ inboundLogisticsVisualDetails }) => renderBoolean(inboundLogisticsVisualDetails?.isLeaking),
        isVisible: ({ machinery, inboundLogisticsVisualDetails }) => machinery
          ? typeof inboundLogisticsVisualDetails?.isLeaking === 'boolean' && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'isLeaking', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Gerät gereinigt',
        key: 'inboundLogisticsVisualDetails',
        render: ({ inboundLogisticsVisualDetails }) => renderBoolean(inboundLogisticsVisualDetails?.isMachineryCleaned),
        isVisible: ({ machinery, inboundLogisticsVisualDetails }) => machinery
          ? typeof inboundLogisticsVisualDetails?.isMachineryCleaned === 'boolean' && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'isMachineryCleaned', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Verschmutzung',
        key: 'inboundLogisticsVisualDetails',
        render: ({ inboundLogisticsVisualDetails }) => logisticsVisualDetailsPollutionToGerman[inboundLogisticsVisualDetails?.pollution as LogisticsVisualDetailsPollution],
        isVisible: ({ machinery, inboundLogisticsVisualDetails }) => machinery
          ? Boolean(inboundLogisticsVisualDetails?.pollution) && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'pollution', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Masseband vorhanden',
        key: 'inboundLogisticsVisualDetails',
        render: ({ inboundLogisticsVisualDetails }) => renderBoolean(inboundLogisticsVisualDetails?.isTapeMeasureAvailable),
        isVisible: ({ machinery, inboundLogisticsVisualDetails }) => machinery
          ? typeof inboundLogisticsVisualDetails?.isTapeMeasureAvailable === 'boolean' && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'isTapeMeasureAvailable', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Bemerkung',
        key: 'inboundLogisticsVisualDetails',
        render: ({ inboundLogisticsVisualDetails }) => inboundLogisticsVisualDetails?.note,
        isVisible: ({ machinery, inboundLogisticsVisualDetails }) => machinery
          ? Boolean(inboundLogisticsVisualDetails?.note) && useShowFieldByFilters(logisticsVisualDetailsFieldsByMachineCategory, 'note', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Zustand',
        key: 'inboundLogisticsConditionDetails',
        isHeading: true,
      },
      {
        title: 'Reifen Luft / SE',
        key: 'inboundLogisticsConditionDetails',
        render: ({ inboundLogisticsConditionDetails }) => logisticsConditionRatingToGerman[inboundLogisticsConditionDetails?.tiresAirAndSE as LogisticsConditionRating],
        isVisible: ({ machinery, inboundLogisticsConditionDetails }) => machinery
          ? Boolean(inboundLogisticsConditionDetails?.tiresAirAndSE) && useShowFieldByFilters(logisticsConditionDetailsFieldsByMachineCategory, 'tiresAirAndSE', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Gabeln / Schaufeln',
        key: 'inboundLogisticsConditionDetails',
        render: ({ inboundLogisticsConditionDetails }) => logisticsConditionRatingToGerman[inboundLogisticsConditionDetails?.forkesAndShovels as LogisticsConditionRating],
        isVisible: ({ machinery, inboundLogisticsConditionDetails }) => machinery
          ? Boolean(inboundLogisticsConditionDetails?.forkesAndShovels) && useShowFieldByFilters(logisticsConditionDetailsFieldsByMachineCategory, 'forkesAndShovels', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Lackierung',
        key: 'inboundLogisticsConditionDetails',
        render: ({ inboundLogisticsConditionDetails }) => logisticsConditionRatingToGerman[inboundLogisticsConditionDetails?.painting as LogisticsConditionRating],
        isVisible: ({ machinery, inboundLogisticsConditionDetails }) => machinery
          ? Boolean(inboundLogisticsConditionDetails?.painting) && useShowFieldByFilters(logisticsConditionDetailsFieldsByMachineCategory, 'painting', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Fahrersitz',
        key: 'inboundLogisticsConditionDetails',
        render: ({ inboundLogisticsConditionDetails }) => logisticsConditionRatingToGerman[inboundLogisticsConditionDetails?.driverSeat as LogisticsConditionRating],
        isVisible: ({ machinery, inboundLogisticsConditionDetails }) => machinery
          ? Boolean(inboundLogisticsConditionDetails?.driverSeat) && useShowFieldByFilters(logisticsConditionDetailsFieldsByMachineCategory, 'driverSeat', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
      {
        title: 'Bemerkung',
        key: 'inboundLogisticsConditionDetails',
        render: ({ inboundLogisticsConditionDetails }) => inboundLogisticsConditionDetails?.note,
        isVisible: ({ machinery, inboundLogisticsConditionDetails }) => machinery
          ? Boolean(inboundLogisticsConditionDetails?.note) && useShowFieldByFilters(logisticsConditionDetailsFieldsByMachineCategory, 'note', machinery.category as MachineryCategories, machinery.drive.name as MachineryDrives)
          : false,
      },
    ]

    const logisticsIssuanceAccessoryDetails: OneDataColumn<LogisticsTaskGetByCuidPositionToShip | LogisticsTaskItemSetMemberPosition>[] = [
      {
        title: 'Geräte Nr.',
        key: 'machineryAccessoryId',
        minWidth: 200,
      },
      {
        title: 'Gerät komplett',
        key: 'outboundLogisticsAccessoryDetails',
        render: ({ outboundLogisticsAccessoryDetails }) => renderBoolean(outboundLogisticsAccessoryDetails?.isMachineryAccessoryComplete),
        isVisible: ({ outboundLogisticsAccessoryDetails }) => typeof outboundLogisticsAccessoryDetails?.isMachineryAccessoryComplete === 'boolean',
      },
      {
        title: 'Äußere Schäden',
        key: 'outboundLogisticsAccessoryDetails',
        render: ({ outboundLogisticsAccessoryDetails }) => renderBoolean(outboundLogisticsAccessoryDetails?.hasExternalDamage),
        isVisible: ({ outboundLogisticsAccessoryDetails }) => typeof outboundLogisticsAccessoryDetails?.hasExternalDamage === 'boolean',
      },
      {
        title: 'Funktionsprüfung',
        key: 'outboundLogisticsAccessoryDetails',
        render: ({ outboundLogisticsAccessoryDetails }) => renderBoolean(outboundLogisticsAccessoryDetails?.isFunctioning),
        isVisible: ({ outboundLogisticsAccessoryDetails }) => typeof outboundLogisticsAccessoryDetails?.isFunctioning === 'boolean',
      },
      {
        title: 'Bemerkung',
        key: 'outboundLogisticsAccessoryDetails',
        render: ({ outboundLogisticsAccessoryDetails }) => outboundLogisticsAccessoryDetails?.note,
        isVisible: ({ outboundLogisticsAccessoryDetails }) => Boolean(outboundLogisticsAccessoryDetails?.note),
      },
    ]

    const logisticsReturnAccessoryDetails: OneDataColumn<LogisticsTaskGetByCuidPositionToShip | LogisticsTaskItemSetMemberPosition>[] = [
      {
        title: 'Geräte Nr.',
        key: 'machineryAccessoryId',
        minWidth: 200,
      },
      {
        title: 'Gerät komplett',
        key: 'inboundLogisticsAccessoryDetails',
        render: ({ inboundLogisticsAccessoryDetails }) => renderBoolean(inboundLogisticsAccessoryDetails?.isMachineryAccessoryComplete),
        isVisible: ({ inboundLogisticsAccessoryDetails }) => typeof inboundLogisticsAccessoryDetails?.isMachineryAccessoryComplete === 'boolean',
      },
      {
        title: 'Äußere Schäden',
        key: 'inboundLogisticsAccessoryDetails',
        render: ({ inboundLogisticsAccessoryDetails }) => renderBoolean(inboundLogisticsAccessoryDetails?.hasExternalDamage),
        isVisible: ({ inboundLogisticsAccessoryDetails }) => typeof inboundLogisticsAccessoryDetails?.hasExternalDamage === 'boolean',
      },
      {
        title: 'Funktionsprüfung',
        key: 'inboundLogisticsAccessoryDetails',
        render: ({ inboundLogisticsAccessoryDetails }) => renderBoolean(inboundLogisticsAccessoryDetails?.isFunctioning),
        isVisible: ({ inboundLogisticsAccessoryDetails }) => typeof inboundLogisticsAccessoryDetails?.isFunctioning === 'boolean',
      },
      {
        title: 'Bemerkung',
        key: 'inboundLogisticsAccessoryDetails',
        render: ({ inboundLogisticsAccessoryDetails }) => inboundLogisticsAccessoryDetails?.note,
        isVisible: ({ inboundLogisticsAccessoryDetails }) => Boolean(inboundLogisticsAccessoryDetails?.note),
      },
    ]

    const configs = {
      logisticsIssuanceAndMachineryDetails,
      logisticsIssuanceAccessoryDetails,
      logisticsReturnAndMachineryDetails,
      logisticsReturnAccessoryDetails,
    }

    return configs
}
